/**
 * The rail's tab strip (fix D / R3). It replaces the "MOVES" kicker and the
 * "← → to browse" hint of M1a (R6.2): the tab IS the label, and the keyboard
 * hint moved into the Info tab.
 *
 * A strip and not a card: one row of text labels over the `line` hairline, the
 * active one carrying a 2 px `pri` underline — the rail is ONE surface (R6.1).
 *
 * Fix G (G2, 2026-10-06): the tournament tab set can run to six (Moves · Info ·
 * Details · Arena · Games · Leaderboard), which does not always fit the 480 px
 * wide rail at the compact `labelSm` size — the row already scrolls
 * horizontally with no indicator; this adds the other half of that contract,
 * scrolling the SELECTED tab into view whenever it changes (a tab press, or a
 * programmatic `setRailTab` such as a game spawn landing on Moves). Each tab
 * reports its own `x`/`width` through `onLayout`; the scroll target is the
 * tab's left edge minus one `SPACE.sm`, clamped to 0 by the ScrollView itself
 * when the tab is already near the start — good enough to guarantee the active
 * tab is on screen without a second measurement pass for true centring.
 */
import React, {useCallback, useEffect, useRef} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';

import {SPACE} from '../../../theme/tokens';
import {useV2Theme} from '../../../theme/useV2Theme';
import {Txt} from '../../../ui';
import {RailTabsModel} from './model';

/** the active tab's underline */
const UNDERLINE = 2;
const STRIP_HEIGHT = 40;

export default React.memo(function RailTabsView({
  tabs,
  active,
  onPress,
}: RailTabsModel) {
  const tokens = useV2Theme(s => s.tokens);
  const scrollRef = useRef<ScrollView>(null);
  const layouts = useRef<Record<string, number>>({});
  // the active key, readable from the stable layout callback (G review: on the
  // FIRST mount the effect below runs before any tab has reported its x, so a
  // default tab past the fold — an arena's Leaderboard — was never scrolled to)
  const activeRef = useRef(active);
  activeRef.current = active;

  const scrollTo = useCallback((x: number) => {
    scrollRef.current?.scrollTo({x: Math.max(0, x - SPACE.sm), animated: true});
  }, []);

  const onTabLayout = useCallback(
    (key: string) => (event: any) => {
      const x = event.nativeEvent.layout.x;
      layouts.current[key] = x;
      if (key === activeRef.current) {
        scrollTo(x);
      }
    },
    [scrollTo],
  );

  useEffect(() => {
    const x = layouts.current[active];
    if (x === undefined) {
      return; // not laid out yet — `onTabLayout` scrolls when it is
    }
    scrollTo(x);
  }, [active, scrollTo]);

  return (
    <View
      testID="v2-rail-tabs"
      style={[
        styles.strip,
        {boxShadow: `inset 0 -1px 0 ${tokens.line}`} as any,
      ]}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.content}>
        {tabs.map(tab => (
          <Pressable
            key={tab.key}
            onLayout={onTabLayout(tab.key)}
            onPress={() => onPress(tab.key)}
            style={({hovered}: any) => [
              styles.tab,
              {
                borderBottomColor:
                  tab.key === active ? tokens.pri : 'transparent',
                backgroundColor: hovered ? tokens.well : 'transparent',
              },
            ]}>
            <Txt
              variant="labelSm"
              tone={tab.key === active ? 'ink' : 'muted'}
              numberOfLines={1}>
              {tab.label}
            </Txt>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  strip: {
    height: STRIP_HEIGHT,
    flexGrow: 0,
    flexShrink: 0,
  },
  content: {
    alignItems: 'stretch',
    paddingHorizontal: SPACE.sm,
  },
  tab: {
    paddingHorizontal: SPACE.md,
    justifyContent: 'center',
    borderBottomWidth: UNDERLINE,
  },
});
